import { useLocation } from 'react-router-dom';

import { useEffect, useRef } from 'react';

import { useUIStore } from '@/stores/uiStore';

// Type for window with loader function
interface WindowWithLoader extends Window {
  __hideInitialLoader?: () => void;
}

/**
 * Hook to handle route changes and initial loader
 * Replaces the RouteChangeContext with Zustand store
 */
export function useRouteChangeHandler() {
  const location = useLocation();
  const pathname = location.pathname;
  const previousPathname = useRef(pathname);
  const mounted = useRef(false);
  const endRouteChange = useUIStore(state => state.endRouteChange);

  // Hide initial HTML loader when React mounts
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;

      // Hide the HTML initial loader
      if (
        typeof window !== 'undefined' &&
        (window as WindowWithLoader).__hideInitialLoader
      ) {
        (window as WindowWithLoader).__hideInitialLoader?.();
      }
    }
  }, []);

  // Detect route changes
  useEffect(() => {
    if (pathname !== previousPathname.current) {
      previousPathname.current = pathname;
      endRouteChange();
    }
  }, [pathname, endRouteChange]);
}

/**
 * Hook to access route change state and actions
 * Compatible API with the old useRouteChange hook
 */
export function useRouteChange() {
  const isChangingRoute = useUIStore(state => state.isChangingRoute);
  const startRouteChange = useUIStore(state => state.startRouteChange);
  const endRouteChange = useUIStore(state => state.endRouteChange);

  return {
    isChangingRoute,
    startRouteChange,
    endRouteChange,
  };
}
